import { Dispatch, SetStateAction, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

// local imports
import loadingCalendar from 'assets/icons/loading-calendar.png'
import successImage from 'assets/images/success-image.svg'
import { ISideBarModel } from 'types/CalendarType'
import AuthUtil from 'utils/AuthUtil'
import FormModal from 'components/ui/modal/FormModal'
import Loader from 'components/ui/Loader'
import NotificationDialog from 'components/ui/NotificationDialog'
import { SearchModalHeader } from 'components/home/on-campus-services/coiffeur/coiffeur-search-modal/styled'
import useGetGymLocationList from 'components/home/on-campus-services/gym/hooks/use-get-gym-locations'
import CalendarGymSearchForm from './CalendarGymSearchForm'
import {
  ICalendarGymSearchForm,
  IFilteredGymGroupList,
  IFilteredGymSalonList,
  IFilteredGymTrainerList,
} from 'types/GymType'

import useGymSalonReservationModal from 'components/home/on-campus-services/gym/hooks/use-gym-salon-reservation-modal'
import GymPersonalResModal from './personal/GymPersonalResModal'
import GroupLessonReservationSidebar from 'components/home/on-campus-services/gym/gym-list/group/GroupLessonReservationSidebar'
import GymTrainerResModal from './trainer/GymTrainerResModal'
import useGetGymServices from './hooks/use-get-gym-services'

interface IGymSearchModalProps {
  modal: boolean
  setModal: Dispatch<SetStateAction<boolean>>
  closeGymModal: () => void
  selectedDay: ISideBarModel | undefined
  resetAll: Dispatch<SetStateAction<boolean>>
  sidebarAfterSave: () => void
}

export default function CalendarGymSearchModal({
  modal,
  setModal,
  selectedDay,
  resetAll,
  closeGymModal,
  sidebarAfterSave,
}: IGymSearchModalProps) {
  const { t } = useTranslation()
  const selectedDayDate = moment(selectedDay?.day.date).toDate()
  const selectedDateStr = moment(selectedDayDate).format('DD.MM.YYYY')

  /* Get Current logged employee infos - (location id) */
  let employeeLocation: number = 0
  const currentEmployee = AuthUtil.getEmployeeSummary()
  if (currentEmployee) employeeLocation = currentEmployee.locationId

  /* Form  */
  /* Filter form default values */
  const defaultValues: ICalendarGymSearchForm = {
    locationId: employeeLocation,
    reservationType: null,
    gymSalon: null,
    groupLesson: null,
    trainer: null,
    reservationDate: selectedDayDate,
  }
  const filteredDate = defaultValues.reservationDate

  /* Search form state  */
  const { watch, resetField, control } = useForm<ICalendarGymSearchForm>({
    defaultValues,
  })
  const locationId = watch('locationId')
  const resType = watch('reservationType')
  const _selectedGymSalon = watch('gymSalon')
  const _selectedGroupLesson = watch('groupLesson')
  const _selectedTrainer = watch('trainer')
  const reservationDate = watch('reservationDate')

  /* Get Gym service Locations | Custom Hooks */
  const { data: locationList } = useGetGymLocationList()

  /* Get Gym Salons -Group Lessons - Gym Trainer List */
  const { gymSalonList, groupLessonList, trainerList, loading } = useGetGymServices(
    locationId,
    reservationDate,
    resType
  )

  /* ################## Personal Reservation Modal ##################### */
  const {
    openModal: openReservationModal,
    closeModal: closeReservationModal,
    modal: reservationModal,
    selectedSalon,
    notification,
    setNotification,
  } = useGymSalonReservationModal<IFilteredGymSalonList>()

  /* Search Modal Methods */
  const continuePersonalRes = () => {
    if (!!_selectedGymSalon) {
      openReservationModal(_selectedGymSalon)
      setModal(false)
    }
  }

  /* Reservation Scheduler Modal */
  const handleOnCancelPersonalResModal = () => {
    closeReservationModal()
    setModal(true)
  }
  /* ################## Personal Reservation Modal ##################### */

  /* ################################## Group Lessons ################################## */
  const [selectedLesson, setSelectedLesson] = useState<IFilteredGymGroupList | null>()
  const [groupSSVisible, setGroupSSVisible] = useState(false)
  const continueGroupRes = () => {
    setModal(false)
    setGroupSSVisible(true)
    setSelectedLesson(_selectedGroupLesson)
  }
  const closeAfterSave = () => {
    setGroupSSVisible(false)
    setNotification(true)
    sidebarAfterSave()
  }
  /* ################################## Group Lessons ################################## */

  /* ################################## GYM Trainer  ################################## */
  const {
    openModal: openPTReservationModal,
    closeModal: closePTReservationModal,
    modal: PTreservationModal,
    selectedSalon: selectedTrainer,
    setSelectedSalon: setSelectedTrainer,
  } = useGymSalonReservationModal<IFilteredGymTrainerList>()

  const continueTrainerRes = () => {
    if (!!_selectedTrainer) {
      openPTReservationModal(_selectedTrainer)
      setModal(false)
    }
  }

  /* Reservation Scheduler Modal */
  const handleOnCancelPTResModal = () => {
    closePTReservationModal()
    setModal(true)
  }

  /* ################################## GYM Trainer  ################################## */

  /* Common Methods - Variables */
  const handleOnCancel = () => closeGymModal()

  const handleCloseNotification = () => {
    setModal(false)
    setNotification(false)
    resetAll(false)
  }

  const btnDisabled =
    resType === 'personal' ? !_selectedGymSalon : resType === 'group' ? !_selectedGroupLesson : !_selectedTrainer

  const handleContinueRes = () => {
    if (resType === 'personal') return continuePersonalRes()
    else if (resType === 'group') return continueGroupRes()
    else return continueTrainerRes()
  }

  return (
    <>
      <FormModal
        id="gymSelectionModal"
        closable
        header={<span className="dialog-header">{t('dialog-texts.gym-reservation-header')} (1/2)</span>}
        disabled={btnDisabled}
        onSave={handleContinueRes}
        visible={modal}
        onHide={handleOnCancel}
        onCancel={handleOnCancel}>
        <SearchModalHeader style={{ marginBottom: '8px' }}>
          <div className="dialog-subheader">
            <span>{t('calendar.selected-day-info-text', { date: selectedDateStr })}</span>
          </div>
        </SearchModalHeader>

        {/* Search Form Modal */}
        <CalendarGymSearchForm
          watch={watch}
          control={control}
          resetField={resetField}
          locationList={locationList}
          gymSalonList={gymSalonList}
          groupLessonList={groupLessonList}
          trainerList={trainerList}
        />

        {loading ? (
          <div className="my-3 w-100">
            <Loader src={loadingCalendar} />
          </div>
        ) : null}
      </FormModal>

      {/* Personal Reservation Timetable Modal */}
      {selectedSalon ? (
        <GymPersonalResModal
          open={reservationModal}
          cancelReservationModal={handleOnCancelPersonalResModal}
          openModal={openReservationModal}
          closeModal={closeReservationModal}
          showNotification={() => {
            setNotification(true)
            sidebarAfterSave()
          }}
          selectedSalon={selectedSalon}
          filterDate={filteredDate}
          selectedDateStr={selectedDateStr}
        />
      ) : null}

      {/* Group Lesson Sidesheet */}
      {selectedLesson && (
        <GroupLessonReservationSidebar
          onCalendar
          closeSideSheet={() => {
            setGroupSSVisible(false)
            setModal(true)
          }}
          groupLessonInfo={selectedLesson}
          setVisible={setGroupSSVisible}
          visible={groupSSVisible}
          closeAfterSave={closeAfterSave}
        />
      )}

      {/* Trainer Reservation Timetable Modal */}
      {!!selectedTrainer ? (
        <GymTrainerResModal
          selectedDateStr={selectedDateStr}
          cancelReservationModal={handleOnCancelPTResModal}
          trainers={trainerList}
          open={PTreservationModal}
          openModal={openPTReservationModal}
          closeModal={closePTReservationModal}
          showNotification={() => {
            setNotification(true)
            sidebarAfterSave()
          }}
          selectedTrainer={selectedTrainer}
          setSelectedTrainer={setSelectedTrainer}
          filterDate={filteredDate}
        />
      ) : null}

      <NotificationDialog
        display={notification}
        imgSrc={successImage}
        goHomeButton={true}
        title={t('success-text.appointment-completed')}
        content={t('gym-res-modal.notification-text')}
        firstButtonLabel={t('buttons.back-to-calendar')}
        onHide={handleCloseNotification}
        onSubmit={handleCloseNotification}
        selectedType={null}
        setSelectedType={undefined}
        types={[]}
      />
    </>
  )
}
